/**
 * InactivityContext - Framework-level inactivity monitoring solution
 * 
 * This provides a React Context API for inactivity monitoring that can be used
 * throughout the application. It wraps the AutoLogout service and provides
 * hooks for components to interact with inactivity monitoring.
 * 
 * Benefits:
 * - Centralized inactivity management
 * - Easy to extend with custom inactivity handlers
 * - Can be used by any component via useInactivity hook
 * - Automatically integrates with authentication state
 */

import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import AutoLogout from '../services/autologout.service';
import { AuthService } from '../services/auth.service';
import { ACCESS_TOKEN } from '../constants';
import logger from '../utils/logger';

interface InactivityContextType {
  /**
   * Reset the inactivity timer (useful when performing important operations)
   */
  resetTimer: () => void;
  
  /**
   * Manually trigger activity detection (useful for programmatic interactions)
   */
  recordActivity: () => void;
  
  /**
   * Get the last activity timestamp
   */
  getLastActiveTime: () => number | null;
  
  /**
   * Check if inactivity monitoring is active
   */
  isMonitoring: boolean;
}

const InactivityContext = createContext<InactivityContextType | null>(null);

interface InactivityProviderProps {
  children: ReactNode;
  /**
   * Timeout in minutes before auto-logout (default: 10)
   */
  timeoutMinutes?: number;
  /**
   * Callback when user is about to be logged out (1 minute before)
   */
  onWarning?: (remainingSeconds: number) => void;
  /**
   * Callback when user is logged out due to inactivity
   */
  onLogout?: () => void;
}

/**
 * InactivityProvider - Provides inactivity monitoring context to child components
 * 
 * Usage:
 * ```tsx
 * <InactivityProvider timeoutMinutes={15}>
 *   <App />
 * </InactivityProvider>
 * ```
 */
export const InactivityProvider: React.FC<InactivityProviderProps> = ({
  children,
  timeoutMinutes = 10,
  onWarning,
  onLogout
}) => {
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [hasToken, setHasToken] = useState(!!AuthService.token);
  const autoLogoutRef = useRef<AutoLogout | null>(null);
  const lastActiveRef = useRef<number>(Date.now());

  // Poll for token presence — AuthService.token is a localStorage getter, not reactive state.
  // This catches the login flow where the token appears after initial render.
  useEffect(() => {
    const checkToken = setInterval(() => {
      const tokenExists = !!AuthService.token;
      setHasToken(prev => {
        if (prev !== tokenExists) return tokenExists;
        return prev;
      });
    }, 2000);
    return () => clearInterval(checkToken);
  }, []);

  useEffect(() => {
    // ⚠️ DO NOT start inactivity monitoring when this React app instance is
    // running INSIDE an iframe (e.g. the cross-storage helper at the /iframe
    // route, which the component editor embeds). The user's keyboard/mouse
    // activity happens in the PARENT document — this nested instance sees
    // zero events, its idle timer runs unchecked, and after ~45 minutes it
    // fires `logOut()` which clears the same-origin localStorage tokens, so
    // BOTH the iframe AND the parent session die. The bug reproduced as
    // "Inactivity timeout (2702s idle on /iframe)" while the user was actively
    // typing in the activity-edit modal. The parent's own InactivityProvider
    // is the single source of truth for the user's session lifetime.
    let runningInIframe = false;
    try {
      runningInIframe = typeof window !== 'undefined' && window.top !== window;
    } catch {
      // Accessing window.top across origins can throw — that itself confirms we're framed.
      runningInIframe = true;
    }
    if (runningInIframe) {
      logger.info('INACTIVITY_CONTEXT', 'Inside iframe — skipping inactivity monitoring (parent owns the timer)');
      return;
    }

    // Initialize AutoLogout service
    if (!autoLogoutRef.current) {
      autoLogoutRef.current = AutoLogout.getInstance() || new AutoLogout(timeoutMinutes);
      logger.info('INACTIVITY_CONTEXT', 'AutoLogout instance created', {
        instanceExists: !!AutoLogout.getInstance(),
        hasToken
      });
    }

    const instance = autoLogoutRef.current;

    // Start monitoring if user is authenticated
    if (hasToken && !isMonitoring) {
      logger.info('INACTIVITY_CONTEXT', 'User authenticated - starting monitoring');
      if (instance) {
        instance.start();
        setIsMonitoring(true);
        lastActiveRef.current = Date.now();
      } else {
        logger.error('INACTIVITY_CONTEXT', 'No instance available to start');
      }
    } else if (!hasToken && isMonitoring) {
      logger.info('INACTIVITY_CONTEXT', 'User not authenticated - stopping monitoring');
      if (instance) {
        instance.stop();
        setIsMonitoring(false);
      }
    }
  }, [timeoutMinutes, isMonitoring, hasToken]);

  const resetTimer = () => {
    if (autoLogoutRef.current) {
      autoLogoutRef.current.resetTimer();
      lastActiveRef.current = Date.now();
      logger.debug('INACTIVITY_CONTEXT', 'Timer manually reset via context');
    }
  };

  const recordActivity = () => {
    resetTimer();
    logger.debug('INACTIVITY_CONTEXT', 'Activity manually recorded via context');
  };

  const getLastActiveTime = (): number | null => {
    return lastActiveRef.current || null;
  };

  const value: InactivityContextType = {
    resetTimer,
    recordActivity,
    getLastActiveTime,
    isMonitoring
  };

  return (
    <InactivityContext.Provider value={value}>
      {children}
    </InactivityContext.Provider>
  );
};

/**
 * useInactivity - Hook to access inactivity monitoring context
 * 
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   const { resetTimer, recordActivity, isMonitoring } = useInactivity();
 *   
 *   const handleImportantAction = () => {
 *     recordActivity(); // Reset timer when performing important action
 *     // ... perform action
 *   };
 *   
 *   return <button onClick={handleImportantAction}>Important Action</button>;
 * }
 * ```
 */
export const useInactivity = (): InactivityContextType => {
  const context = useContext(InactivityContext);
  
  if (!context) {
    throw new Error('useInactivity must be used within an InactivityProvider');
  }
  
  return context;
};

/**
 * withInactivity - Higher-Order Component (HOC) for components that need inactivity monitoring
 * 
 * Usage:
 * ```tsx
 * const MyComponentWithInactivity = withInactivity(MyComponent);
 * ```
 */
export const withInactivity = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P & { inactivity: InactivityContextType }> => {
  return (props: P) => {
    const inactivity = useInactivity();
    return <Component {...props} inactivity={inactivity} />;
  };
};

export default InactivityContext;
